import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnConfig, ProcessedPlatformProduct, CurrencyType, EmirTipi } from '../types';
import { CURRENCY_CODES, CURRENCY_SYMBOLS } from '../types';
import { DataTable } from '../components/DataTable';
import { BulkUploadModal } from '../components/BulkUploadModal';
import { useLanguage } from '../i18n';
import { useAuth } from '../context/AuthContext';
import { productsApi, befelsApi } from '../services/api';
import { useSEO } from '../hooks/useSEO';
import styles from './AddOrdersPage.module.css';

// Filter state interface
interface ProductFilters {
  barcode: string;
  name: string;
  modelKodu: string;
  sku: string;
  category: string;
  brand: string;
}

// Platform configuration - order matters!
interface PlatformConfig {
  id: string;
  name: string;
  enabled: boolean;
}

const PLATFORMS: PlatformConfig[] = [
  { id: 'TRENDYOL', name: 'Trendyol', enabled: true },
  { id: 'N11', name: 'N11', enabled: false },
  { id: 'AMAZONTR', name: 'Amazon TR', enabled: false },
  { id: 'HEPSIBURADA', name: 'Hepsiburada', enabled: false },
];

// Platform display names for processing
const PLATFORM_NAMES: Record<string, string> = {
  'TRENDYOL': 'Trendyol',
  'N11': 'N11',
  'AMAZONTR': 'Amazon TR',
  'HEPSIBURADA': 'Hepsiburada',
};

interface OrderFormData {
  product: ProcessedPlatformProduct | null;
  currency: CurrencyType | null;
  minPrice: string;
  maxPrice: string;
  orderType: EmirTipi | null;
  priceIncludesCommission: boolean;
  activateCoupon: boolean;
  activatePromotion: boolean;
}

interface FormErrors {
  currency?: string;
  minPrice?: string;
  maxPrice?: string;
  orderType?: string;
}

// Approximate exchange rates to TRY (for price bounds validation)
// These are rough estimates used only for warning the user
// Must match BefelMaster defaults for consistency
const CURRENCY_TO_TRY: Record<CurrencyType, number> = {
  0: 1,      // TRY
  1: 42.8,   // USD -> TRY
  2: 50.6,   // EUR -> TRY
  3: 6.1,    // CNY -> TRY
};

export function AddOrdersPage() {
  // Private page - noindex
  useSEO({
    title: 'Emir Ekle',
    description: 'Polaris Emir Ekle',
    noindex: true,
  });
  const { token } = useAuth();
  const { t } = useLanguage();

  // Products state - all products loaded at once
  const [allProducts, setAllProducts] = useState<ProcessedPlatformProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<string>('');

  // Filter options from server
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [availableBrands, setAvailableBrands] = useState<string[]>([]);

  // Filter state - multiple search fields
  const [filters, setFilters] = useState<ProductFilters>({
    barcode: '',
    name: '',
    modelKodu: '',
    sku: '',
    category: '',
    brand: '',
  });

  // Client-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage, setProductsPerPage] = useState(20);
  const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

  // Order form state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState<OrderFormData>({
    product: null,
    currency: null,
    minPrice: '',
    maxPrice: '',
    orderType: null,
    priceIncludesCommission: true,
    activateCoupon: false,
    activatePromotion: false,
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [priceWarning, setPriceWarning] = useState<string | null>(null);
  const [priceWarningAccepted, setPriceWarningAccepted] = useState(false);

  // Bulk upload modal state
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);

  // Reset state when platform changes
  useEffect(() => {
    setCurrentPage(1);
    setAllProducts([]);
    setAvailableCategories([]);
    setAvailableBrands([]);
    setFilters({
      barcode: '',
      name: '',
      modelKodu: '',
      sku: '',
      category: '',
      brand: '',
    });
  }, [selectedPlatform]);

  // Fetch ALL products when platform is selected (backend fetches all pages)
  useEffect(() => {
    const fetchAllProducts = async () => {
      if (!token || !selectedPlatform) {
        setAllProducts([]);
        return;
      }

      setIsLoading(true);
      const response = await productsApi.getAllProducts(token, selectedPlatform);
      if (response.data) {
        const { products: serverProducts, categories, brands } = response.data;
        const processed: ProcessedPlatformProduct[] = serverProducts.map((p) => ({
          id: p.id,
          platform: p.platform,
          platformDisplayName: PLATFORM_NAMES[p.platform] || p.platform,
          name: p.name,
          sku: p.sku,
          barcode: p.barcode,
          modelKodu: p.modelKodu,
          currentPrice: p.currentPrice,
          currency: p.currency as CurrencyType,
          currencyCode: CURRENCY_CODES[p.currency as CurrencyType] || 'TRY',
          currencySymbol: CURRENCY_SYMBOLS[p.currency as CurrencyType] || '₺',
          stock: p.stock,
          url: p.url,
          attributes: p.attributes,
          images: p.images,
          categoryName: p.categoryName,
          brandName: p.brandName,
          isSalable: p.isSalable,
          isLocked: p.isLocked,
          isSuspended: p.isSuspended,
          isFrozen: p.isFrozen,
        }));
        setAllProducts(processed);
        setAvailableCategories(categories);
        setAvailableBrands(brands);
      } else {
        setAllProducts([]);
        setAvailableCategories([]);
        setAvailableBrands([]);
      }
      setIsLoading(false);
    };

    fetchAllProducts();
  }, [token, selectedPlatform]);

  // Update filter handler
  const handleFilterChange = useCallback((field: keyof ProductFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  }, []);

  // Clear all filters
  const handleClearFilters = useCallback(() => {
    setFilters({
      barcode: '',
      name: '',
      modelKodu: '',
      sku: '',
      category: '',
      brand: '',
    });
    setCurrentPage(1);
  }, []);

  // Filter products by all filter fields (client-side)
  const filteredProducts = useMemo(() => {
    return allProducts.filter((p) => {
      // Barcode filter
      if (filters.barcode && p.barcode && !p.barcode.toLowerCase().includes(filters.barcode.toLowerCase())) {
        return false;
      }
      if (filters.barcode && !p.barcode) {
        return false;
      }

      // Name filter
      if (filters.name && !p.name.toLowerCase().includes(filters.name.toLowerCase())) {
        return false;
      }

      // Model Kodu filter
      if (filters.modelKodu && p.modelKodu && !p.modelKodu.toLowerCase().includes(filters.modelKodu.toLowerCase())) {
        return false;
      }
      if (filters.modelKodu && !p.modelKodu) {
        return false;
      }

      // SKU (Stok Kodu) filter
      if (filters.sku && !p.sku.toLowerCase().includes(filters.sku.toLowerCase())) {
        return false;
      }

      // Category filter (exact match from dropdown)
      if (filters.category && p.categoryName !== filters.category) {
        return false;
      }

      // Brand filter (exact match from dropdown)
      if (filters.brand && p.brandName !== filters.brand) {
        return false;
      }

      return true;
    });
  }, [allProducts, filters]);

  // Paginated products for display
  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * productsPerPage;
    return filteredProducts.slice(startIndex, startIndex + productsPerPage);
  }, [filteredProducts, currentPage, productsPerPage]);

  // Total pages based on filtered products
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage) || 1;

  // Handle page size change
  const handlePageSizeChange = useCallback((newSize: number) => {
    setProductsPerPage(newSize);
    setCurrentPage(1); // Reset to first page when page size changes
  }, []);

  // Currency options
  const currencyOptions: { value: CurrencyType; label: string }[] = [
    { value: 0, label: t.addOrders.currencyTRY },
    { value: 1, label: t.addOrders.currencyUSD },
    { value: 2, label: t.addOrders.currencyEUR },
    { value: 3, label: t.addOrders.currencyYUAN },
  ];

  // Order type options
  const orderTypeOptions: { value: EmirTipi; label: string }[] = [
    { value: 0, label: t.addOrders.buyboxCapture },
    { value: 1, label: t.addOrders.pressureApply },
  ];

  // Open add order form
  const handleAddOrder = (product: ProcessedPlatformProduct) => {
    setFormData({
      product,
      currency: product.currency, // Pre-fill with product's currency
      minPrice: '',
      maxPrice: '',
      orderType: null,
      priceIncludesCommission: true,
      activateCoupon: false,
      activatePromotion: false,
    });
    setFormErrors({});
    setSubmitMessage(null);
    setIsFormOpen(true);
  };

  // Close form
  const handleCloseForm = () => {
    setIsFormOpen(false);
    setFormData({
      product: null,
      currency: null,
      minPrice: '',
      maxPrice: '',
      orderType: null,
      priceIncludesCommission: true,
      activateCoupon: false,
      activatePromotion: false,
    });
    setFormErrors({});
    setSubmitMessage(null);
    setPriceWarning(null);
    setPriceWarningAccepted(false);
  };

  // Validate form
  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    // Currency validation
    if (formData.currency === null) {
      errors.currency = t.addOrders.currencyRequired;
    }

    // Min price validation
    if (!formData.minPrice.trim()) {
      errors.minPrice = t.addOrders.minPriceRequired;
    } else {
      const minPrice = parseFloat(formData.minPrice);
      if (isNaN(minPrice) || minPrice <= 0) {
        errors.minPrice = t.addOrders.minPriceMustBePositive;
      }
    }

    // Max price validation
    if (!formData.maxPrice.trim()) {
      errors.maxPrice = t.addOrders.maxPriceRequired;
    } else {
      const maxPrice = parseFloat(formData.maxPrice);
      if (isNaN(maxPrice) || maxPrice <= 0) {
        errors.maxPrice = t.addOrders.maxPriceMustBePositive;
      }
    }

    // Min vs Max validation (min must be strictly less than max)
    if (formData.minPrice && formData.maxPrice) {
      const minPrice = parseFloat(formData.minPrice);
      const maxPrice = parseFloat(formData.maxPrice);
      if (!isNaN(minPrice) && !isNaN(maxPrice) && minPrice >= maxPrice) {
        errors.minPrice = t.addOrders.minPriceGreaterThanMax;
      }
    }

    // Order type validation
    if (formData.orderType === null) {
      errors.orderType = t.addOrders.orderTypeRequired;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Check price bounds and show warning if needed
  const checkPriceBounds = (): string | null => {
    if (!formData.product || formData.currency === null) return null;

    const minPrice = parseFloat(formData.minPrice);
    const maxPrice = parseFloat(formData.maxPrice);
    if (isNaN(minPrice) || isNaN(maxPrice)) return null;

    // Convert user's prices to TRY for comparison
    const rate = CURRENCY_TO_TRY[formData.currency];
    const minPriceInTry = minPrice * rate;
    const maxPriceInTry = maxPrice * rate;

    // Product price is always in TRY
    const productPrice = formData.product.currentPrice;
    const halfPrice = productPrice / 2;
    const doublePrice = productPrice * 2;

    const warnings: string[] = [];

    if (minPriceInTry < halfPrice) {
      warnings.push(`Alt fiyat (${CURRENCY_SYMBOLS[formData.currency]}${minPrice.toFixed(2)} ≈ ₺${minPriceInTry.toFixed(2)}) ürün fiyatının yarısından (₺${halfPrice.toFixed(2)}) düşük`);
    }
    if (maxPriceInTry > doublePrice) {
      warnings.push(`Üst fiyat (${CURRENCY_SYMBOLS[formData.currency]}${maxPrice.toFixed(2)} ≈ ₺${maxPriceInTry.toFixed(2)}) ürün fiyatının iki katından (₺${doublePrice.toFixed(2)}) yüksek`);
    }

    return warnings.length > 0 ? warnings.join('\n') : null;
  };

  // Submit order
  const handleSubmitOrder = async () => {
    if (!validateForm() || !formData.product || !token) return;

    // Check price bounds warning (only if not already accepted)
    if (!priceWarningAccepted) {
      const warning = checkPriceBounds();
      if (warning) {
        setPriceWarning(warning);
        return; // Don't submit yet, wait for user to accept
      }
    }

    setIsSubmitting(true);
    setSubmitMessage(null);
    setPriceWarning(null);

    const response = await befelsApi.create(token, {
      platform: formData.product.platform,
      urun: formData.product.name,
      sku: formData.product.barcode || '',  // Always use barcode as SKU
      alt: parseFloat(formData.minPrice),
      ust: parseFloat(formData.maxPrice),
      paraTipi: formData.currency!,  // Currency type from user selection
      emirTipi: formData.orderType!,
      url: formData.product.url,  // URL already cleaned by backend (only v= param preserved)
      modelKodu: formData.product.modelKodu,
      priceIncludesCommission: formData.priceIncludesCommission,
      activateCoupon: formData.activateCoupon,
      activatePromotion: formData.activatePromotion,
    });

    setIsSubmitting(false);

    if (response.data) {
      setSubmitMessage({ type: 'success', text: t.addOrders.orderAddedSuccess });
      // Close form after short delay
      setTimeout(() => {
        handleCloseForm();
      }, 1500);
    } else {
      setSubmitMessage({ type: 'error', text: response.error || t.addOrders.orderAddedError });
    }
  };

  // Table columns
  const columns: ColumnConfig<ProcessedPlatformProduct>[] = useMemo(() => [
    {
      key: 'images',
      header: 'Resim',
      width: '60px',
      render: (_, item) => {
        const firstImage = item.images?.[0];
        return firstImage ? (
          <img src={firstImage} alt="" className={styles.productThumbnail} />
        ) : (
          <div className={styles.noImage}>-</div>
        );
      },
    },
    {
      key: 'name',
      header: t.addOrders.productName,
      render: (value, item) => {
        // URL is already cleaned by backend (only v= param preserved)
        return (
          <div className={styles.productNameCell}>
            {item.url ? (
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.productNameLink}
              >
                {value as string}
              </a>
            ) : (
              <span className={styles.productName}>{value as string}</span>
            )}
            <div className={styles.productMeta}>
              {item.modelKodu && (
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>Model Kodu:</span> {item.modelKodu}
                </div>
              )}
              {item.barcode && (
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>Barkod:</span> {item.barcode}
                </div>
              )}
              {item.sku && (
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>Stok Kodu:</span> {item.sku}
                </div>
              )}
              {item.categoryName && (
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>Kategori:</span> {item.categoryName}
                </div>
              )}
              {item.brandName && (
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>Marka:</span> {item.brandName}
                </div>
              )}
              {/* Only show specific variant attributes: Renk, Beden, Koleksiyon, Siluet, Ortam */}
              {item.attributes && Object.entries(item.attributes)
                .filter(([key]) => ['Renk', 'Beden', 'Koleksiyon', 'Siluet', 'Ortam'].includes(key))
                .map(([key, attrValue]) => (
                  <div key={key} className={styles.metaItem}>
                    <span className={styles.metaLabel}>{key}:</span> {attrValue}
                  </div>
                ))}
            </div>
          </div>
        );
      },
    },
    {
      key: 'currentPrice',
      header: t.addOrders.currentPrice,
      width: '120px',
      render: (value, item) => (
        <span className={styles.price}>
          {item.currencySymbol}{(value as number).toFixed(2)}
        </span>
      ),
    },
    {
      key: 'stock',
      header: t.addOrders.stock,
      width: '80px',
      render: (value) => (
        <span className={(value as number) > 0 ? styles.inStock : styles.outOfStock}>
          {value as number}
        </span>
      ),
    },
    {
      key: 'actions',
      header: t.addOrders.actions,
      width: '120px',
      className: styles.actionCell,
      render: (_, item) => (
        <button
          className={styles.addOrderBtn}
          onClick={(e) => {
            e.stopPropagation();
            handleAddOrder(item);
          }}
        >
          {t.addOrders.addOrder}
        </button>
      ),
    },
  ], [t]);

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <p>{t.addOrders.loadingProducts}</p>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <Link to="/" className={styles.backLink}>
          &larr; {t.addOrders.backToDashboard}
        </Link>
        <h1 className={styles.title}>{t.addOrders.title}</h1>
        <p className={styles.subtitle}>{t.addOrders.subtitle}</p>
      </header>

      {/* Platform Selection */}
      <div className={styles.platformSelector}>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>{t.addOrders.selectPlatform}</label>
          <select
            className={styles.select}
            value={selectedPlatform}
            onChange={(e) => setSelectedPlatform(e.target.value)}
          >
            <option value="">{t.addOrders.selectPlatform}...</option>
            {PLATFORMS.map((platform) => (
              <option
                key={platform.id}
                value={platform.id}
                disabled={!platform.enabled}
              >
                {platform.name}{!platform.enabled ? ` (${t.addOrders.comingSoon})` : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Advanced Filters - Trendyol style - only show when platform is selected */}
      {selectedPlatform && (
        <div className={styles.advancedFilters}>
          {/* 3x2 Grid of filters */}
          <div className={styles.filterGrid}>
            {/* Row 1 */}
            <div className={styles.filterField}>
              <input
                type="text"
                className={styles.filterInput}
                placeholder="Barkod"
                value={filters.barcode}
                onChange={(e) => handleFilterChange('barcode', e.target.value)}
              />
            </div>
            <div className={styles.filterField}>
              <input
                type="text"
                className={styles.filterInput}
                placeholder="Ürün Adı"
                value={filters.name}
                onChange={(e) => handleFilterChange('name', e.target.value)}
              />
            </div>
            <div className={styles.filterField}>
              <input
                type="text"
                className={styles.filterInput}
                placeholder="Model Kodu"
                value={filters.modelKodu}
                onChange={(e) => handleFilterChange('modelKodu', e.target.value)}
              />
            </div>
            {/* Row 2 */}
            <div className={styles.filterField}>
              <input
                type="text"
                className={styles.filterInput}
                placeholder="Stok Kodu"
                value={filters.sku}
                onChange={(e) => handleFilterChange('sku', e.target.value)}
              />
            </div>
            <div className={styles.filterField}>
              <select
                className={styles.filterSelect}
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
              >
                <option value="">Kategori</option>
                {availableCategories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div className={styles.filterField}>
              <select
                className={styles.filterSelect}
                value={filters.brand}
                onChange={(e) => handleFilterChange('brand', e.target.value)}
              >
                <option value="">Marka</option>
                {availableBrands.map((brand) => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>
            </div>
          </div>
          {/* Clear button */}
          <div className={styles.filterActions}>
            <button
              className={styles.clearFilterBtn}
              onClick={handleClearFilters}
            >
              Temizle
            </button>
          </div>
        </div>
      )}

      {/* Products table or platform selection prompt */}
      {!selectedPlatform ? (
        <div className={styles.emptyState}>
          <h3>{t.addOrders.selectPlatform}</h3>
          <p>{t.addOrders.selectPlatformDesc}</p>
        </div>
      ) : allProducts.length === 0 && !isLoading ? (
        <div className={styles.emptyState}>
          <h3>{t.addOrders.noProducts}</h3>
          <p>{t.addOrders.noProductsDesc}</p>
        </div>
      ) : (
        <>
          {/* Products header with count and actions */}
          <div className={styles.productsHeader}>
            <div className={styles.productsTitle}>
              <h2>Tüm Ürünler</h2>
              <span className={styles.productCountBadge}>
                {filteredProducts.length} / {allProducts.length} ürün
              </span>
            </div>
            <div className={styles.tableActions}>
              <button
                className={styles.bulkUploadBtn}
                onClick={() => setIsBulkModalOpen(true)}
              >
                {t.bulkUpload.buttonText}
              </button>
            </div>
          </div>

          {/* Pagination controls at top */}
          <div className={styles.paginationHeader}>
            <span className={styles.pageInfoText}>
              Her Sayfada
            </span>
            <select
              className={styles.pageSizeSelect}
              value={productsPerPage}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>{size} Ürün(ler)</option>
              ))}
            </select>
            {totalPages > 1 && (
              <>
                <span className={styles.pageInfoText}>
                  Sayfaya Git
                </span>
                <input
                  type="number"
                  className={styles.pageInput}
                  value={currentPage}
                  min={1}
                  max={totalPages}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val) && val >= 1 && val <= totalPages) {
                      setCurrentPage(val);
                    }
                  }}
                />
              </>
            )}
            <div className={styles.paginationButtons}>
              <button
                className={styles.pageBtn}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
              >
                &lt;
              </button>
              <span className={styles.currentPageIndicator}>{currentPage}</span>
              {totalPages > 1 && (
                <span className={styles.totalPagesText}>/ {totalPages}</span>
              )}
              <button
                className={styles.pageBtn}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
              >
                &gt;
              </button>
            </div>
          </div>

          <DataTable
            data={paginatedProducts}
            columns={columns}
            keyField="id"
            emptyMessage={filteredProducts.length === 0 ? 'Filtreye uygun ürün bulunamadı' : t.addOrders.noProducts}
          />

          {/* Pagination at bottom */}
          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                className={styles.pageBtn}
                onClick={() => setCurrentPage(1)}
                disabled={currentPage <= 1}
                title="İlk Sayfa"
              >
                &laquo;
              </button>
              <button
                className={styles.pageBtn}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                title="Önceki"
              >
                &larr;
              </button>

              <span className={styles.pageInfo}>
                <input
                  type="number"
                  className={styles.pageInput}
                  value={currentPage}
                  min={1}
                  max={totalPages}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val) && val >= 1 && val <= totalPages) {
                      setCurrentPage(val);
                    }
                  }}
                />
                <span className={styles.pageTotal}>/ {totalPages}</span>
                <span className={styles.productCount}>
                  ({filteredProducts.length} ürün)
                </span>
              </span>

              <button
                className={styles.pageBtn}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                title="Sonraki"
              >
                &rarr;
              </button>
              <button
                className={styles.pageBtn}
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage >= totalPages}
                title="Son Sayfa"
              >
                &raquo;
              </button>
            </div>
          )}
        </>
      )}

      {/* Add Order Modal */}
      {isFormOpen && formData.product && (
        <div className={styles.modalOverlay} onClick={handleCloseForm}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{t.addOrders.addOrderTitle}</h2>
              <button className={styles.closeBtn} onClick={handleCloseForm}>
                &times;
              </button>
            </div>

            <div className={styles.modalBody}>
              {/* Product Info */}
              <div className={styles.formSection}>
                <h3>{t.addOrders.productInfo}</h3>
                <div className={styles.productInfoCard}>
                  <div className={styles.productInfoRow}>
                    <span className={styles.label}>{t.emirler.platform}:</span>
                    <span>{formData.product.platformDisplayName}</span>
                  </div>
                  <div className={styles.productInfoRow}>
                    <span className={styles.label}>{t.addOrders.productName}:</span>
                    <span>{formData.product.name}</span>
                  </div>
                  {formData.product.modelKodu && (
                    <div className={styles.productInfoRow}>
                      <span className={styles.label}>Model Kodu:</span>
                      <span>{formData.product.modelKodu}</span>
                    </div>
                  )}
                  {formData.product.barcode && (
                    <div className={styles.productInfoRow}>
                      <span className={styles.label}>Barkod:</span>
                      <span>{formData.product.barcode}</span>
                    </div>
                  )}
                  {formData.product.sku && (
                    <div className={styles.productInfoRow}>
                      <span className={styles.label}>Stok Kodu:</span>
                      <span>{formData.product.sku}</span>
                    </div>
                  )}
                  <div className={styles.productInfoRow}>
                    <span className={styles.label}>{t.addOrders.currentPrice}:</span>
                    <span>{formData.product.currencySymbol}{formData.product.currentPrice.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Price Settings */}
              <div className={styles.formSection}>
                <h3>{t.addOrders.priceSettings}</h3>

                {/* Currency */}
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>{t.addOrders.currency}</label>
                  <select
                    className={`${styles.formSelect} ${formErrors.currency ? styles.error : ''}`}
                    value={formData.currency ?? ''}
                    onChange={(e) => { setFormData({ ...formData, currency: e.target.value === '' ? null : parseInt(e.target.value) as CurrencyType }); setPriceWarningAccepted(false); }}
                  >
                    <option value="">{t.addOrders.selectCurrency}</option>
                    {currencyOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  {formErrors.currency && <span className={styles.errorText}>{formErrors.currency}</span>}
                </div>

                {/* Min/Max Price */}
                <div className={styles.priceInputs}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>{t.addOrders.minPrice}</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className={`${styles.formInput} ${formErrors.minPrice ? styles.error : ''}`}
                      value={formData.minPrice}
                      onChange={(e) => { setFormData({ ...formData, minPrice: e.target.value }); setPriceWarningAccepted(false); }}
                      placeholder="0.00"
                    />
                    {formErrors.minPrice && <span className={styles.errorText}>{formErrors.minPrice}</span>}
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>{t.addOrders.maxPrice}</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className={`${styles.formInput} ${formErrors.maxPrice ? styles.error : ''}`}
                      value={formData.maxPrice}
                      onChange={(e) => { setFormData({ ...formData, maxPrice: e.target.value }); setPriceWarningAccepted(false); }}
                      placeholder="0.00"
                    />
                    {formErrors.maxPrice && <span className={styles.errorText}>{formErrors.maxPrice}</span>}
                  </div>
                </div>

                {/* Order Type */}
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>{t.addOrders.orderType}</label>
                  <select
                    className={`${styles.formSelect} ${formErrors.orderType ? styles.error : ''}`}
                    value={formData.orderType ?? ''}
                    onChange={(e) => setFormData({ ...formData, orderType: e.target.value === '' ? null : parseInt(e.target.value) as EmirTipi })}
                  >
                    <option value="">{t.addOrders.selectOrderType}</option>
                    {orderTypeOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  {formErrors.orderType && <span className={styles.errorText}>{formErrors.orderType}</span>}
                </div>

                {/* Commission included checkbox */}
                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={formData.priceIncludesCommission}
                      onChange={(e) => setFormData({ ...formData, priceIncludesCommission: e.target.checked })}
                      className={styles.checkbox}
                    />
                    <span>{t.addOrders.priceIncludesCommission}</span>
                  </label>
                </div>

                {/* Coupon activation checkbox */}
                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={formData.activateCoupon}
                      onChange={(e) => setFormData({ ...formData, activateCoupon: e.target.checked })}
                      className={styles.checkbox}
                    />
                    <span>{t.addOrders.activateCoupon}</span>
                  </label>
                </div>

                {/* Promotion activation checkbox */}
                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={formData.activatePromotion}
                      onChange={(e) => setFormData({ ...formData, activatePromotion: e.target.checked })}
                      className={styles.checkbox}
                    />
                    <span>{t.addOrders.activatePromotion}</span>
                  </label>
                </div>

                {/* Pricing hint */}
                <div className={styles.pricingHint}>
                  {t.addOrders.pricingHint}
                </div>
              </div>

              {/* Price warning */}
              {priceWarning && (
                <div className={`${styles.message} ${styles.warning}`}>
                  <div style={{ marginBottom: '8px', whiteSpace: 'pre-line' }}>{priceWarning}</div>
                  <button
                    className={styles.warningAcceptBtn}
                    onClick={() => {
                      setPriceWarningAccepted(true);
                      setPriceWarning(null);
                    }}
                  >
                    Kabul Et ve Devam Et
                  </button>
                </div>
              )}

              {/* Submit message */}
              {submitMessage && (
                <div className={`${styles.message} ${styles[submitMessage.type]}`}>
                  {submitMessage.text}
                </div>
              )}
            </div>

            <div className={styles.modalFooter}>
              <button
                className={styles.cancelBtn}
                onClick={handleCloseForm}
                disabled={isSubmitting}
              >
                {t.cancel}
              </button>
              <button
                className={styles.submitBtn}
                onClick={handleSubmitOrder}
                disabled={isSubmitting}
              >
                {isSubmitting ? t.loading : t.addOrders.addOrder}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {token && selectedPlatform && (
        <BulkUploadModal
          isOpen={isBulkModalOpen}
          onClose={() => setIsBulkModalOpen(false)}
          platform={selectedPlatform}
          token={token}
          onSuccess={() => {
            // Optionally refresh data or navigate after successful bulk upload
          }}
        />
      )}
    </div>
  );
}
